/* eslint-disable sonarjs/no-redundant-jump */
import { User, mapSupabaseUserToUser } from '../types';

import { supabase } from './supabase';

const SUPABASE_NOT_INITIALIZED_ERROR = 'Supabase client is not initialized';

/**
 * トークン管理クラス
 * JWTトークンの安全な保存、有効期限チェック、自動更新を管理
 */
export class TokenManager {
  private static readonly REFRESH_THRESHOLD = 5 * 60 * 1000; // 5分前にリフレッシュ
  private refreshTimer: NodeJS.Timeout | null = null;

  /**
   * 現在のセッション情報を取得
   */
  static async getCurrentSession() {
    try {
      if (!supabase) {
        console.error(SUPABASE_NOT_INITIALIZED_ERROR);
        return null;
      }

      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.error('Error getting session:', error);
        return null;
      }

      return session;
    } catch (error) {
      console.error('Error getting current session:', error);
      return null;
    }
  }

  /**
   * トークンの有効期限をチェック
   */
  static async isTokenValid(): Promise<boolean> {
    try {
      const session = await this.getCurrentSession();

      if (!session || !session.access_token) {
        return false;
      }

      // トークンの有効期限をチェック
      const expiresAt = session.expires_at;
      if (!expiresAt) {
        return false;
      }

      const now = Math.floor(Date.now() / 1000);
      return expiresAt > now;
    } catch (error) {
      console.error('Error checking token validity:', error);
      return false;
    }
  }

  /**
   * トークンが間もなく期限切れかチェック
   */
  static async isTokenNearExpiry(): Promise<boolean> {
    try {
      const session = await this.getCurrentSession();

      if (!session || !session.expires_at) {
        return true; // セッションがない場合は期限切れとみなす
      }

      const now = Math.floor(Date.now() / 1000);
      const expiresAt = session.expires_at;
      const timeUntilExpiry = (expiresAt - now) * 1000; // ミリ秒に変換

      return timeUntilExpiry <= this.REFRESH_THRESHOLD;
    } catch (error) {
      console.error('Error checking token expiry:', error);
      return true;
    }
  }

  /**
   * トークンを手動でリフレッシュ
   */
  static async refreshToken(): Promise<User | null> {
    try {
      if (!supabase) {
        console.error(SUPABASE_NOT_INITIALIZED_ERROR);
        return null;
      }

      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        console.error('Error refreshing token:', error);
        // eslint-disable-next-line sonarjs/no-redundant-jump
        return null;
      }

      if (!data.user) {
        return null;
      }

      return mapSupabaseUserToUser(data.user);
    } catch (error) {
      console.error('Error during token refresh:', error);
      return null;
    }
  }

  /**
   * 自動リフレッシュを開始（無限ループを防ぐため一時的に無効化）
   */
  // eslint-disable-next-line sonarjs/no-redundant-jump
  startAutoRefresh(onTokenRefreshed?: (user: User | null) => void): void {
    // 無限ループを防ぐため、自動リフレッシュを一時的に無効化
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('Auto refresh temporarily disabled to prevent infinite loops');
    }
    return;
  }

  /**
   * 自動リフレッシュタイマーを停止
   */
  stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * セッション情報をクリア
   */
  static async clearSession(): Promise<void> {
    try {
      if (!supabase) {
        console.error(SUPABASE_NOT_INITIALIZED_ERROR);
        return;
      }

      // Supabaseのセッションをクリア
      await supabase.auth.signOut();

      // ローカルストレージからも明示的にクリア
      if (typeof window !== 'undefined') {
        // Supabaseが使用するキーをクリア
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.startsWith('supabase.auth.')) {
            localStorage.removeItem(key);
          }
        });
      }
    } catch (error) {
      console.error('Error clearing session:', error);
    }
  }

  /**
   * セッション情報の詳細を取得（デバッグ用）
   */
  static async getSessionInfo(): Promise<{
    hasSession: boolean;
    isValid: boolean;
    expiresAt?: number;
    timeUntilExpiry?: number;
  }> {
    try {
      const session = await this.getCurrentSession();

      if (!session) {
        return { hasSession: false, isValid: false };
      }

      const isValid = await this.isTokenValid();
      const expiresAt = session.expires_at;
      const timeUntilExpiry = expiresAt
        ? (expiresAt - Math.floor(Date.now() / 1000)) * 1000
        : undefined;

      return {
        hasSession: true,
        isValid,
        expiresAt,
        timeUntilExpiry,
      };
    } catch (error) {
      console.error('Error getting session info:', error);
      return { hasSession: false, isValid: false };
    }
  }
}

// TokenManagerのインスタンスを作成してエクスポート
export const tokenManager = new TokenManager();
